import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  PaymentType,
  v4Service,
  alfredService,
  CreateSessionResponse,
  AlfredInitiateDepositTransaction,
} from "src/api";
import { onlyNumbers, formatNumberToExchange, generateDepositCallbackUrls, _t } from "src/utils";
import { ERoutes } from "src/types";

interface UseDepositActionsParams {
  amount: string;
  setAmount: (amount: string) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSessionData: (data: CreateSessionResponse) => void;
  setPaymentInstructions: (tx: AlfredInitiateDepositTransaction | null) => void;
  currencyPrecision: number;
  paymentRouteId: string | null;
  /** Fiat the user picked in the widget (drives Alfred /quotes + /onramp pair) */
  sourceCurrencyId: string | null;
  /**
   * Country-specific bank id the user filled in. Required by Alfred
   * for COP onramp; the form supplies it only when the chosen fiat
   * needs it. Forwarded as `metadata.bankId` to /onramp.
   */
  bankId?: string | null;
}

export const useDepositActions = (params: UseDepositActionsParams) => {
  const {
    amount,
    setAmount,
    setIsLoading,
    setError,
    setSessionData,
    setPaymentInstructions,
    currencyPrecision,
    paymentRouteId,
    sourceCurrencyId,
    bankId,
  } = params;

  const navigate = useNavigate();

  /**
   * Handles amount input change with validation
   */
  const handleAmountChange = useCallback(
    (newAmount: string) => {
      if (onlyNumbers(newAmount)) {
        setAmount(formatNumberToExchange(newAmount, currencyPrecision));
      }
    },
    [currencyPrecision, setAmount]
  );

  /**
   * Creates a session for the deposit
   */
  const createSession = useCallback(
    async (amount: string) => {
      if (!paymentRouteId) {
        throw new Error(_t("ERRORS.PAYMENT_ROUTE_ID_REQUIRED"));
      }
      if (!sourceCurrencyId) {
        throw new Error(_t("ERRORS.SOURCE_CURRENCY_REQUIRED"));
      }

      return await v4Service.createSession({
        data: {
          amount: Number(amount),
          payment_route_id: paymentRouteId,
          source_currency_id: sourceCurrencyId,
          type: PaymentType.Deposit,
        },
      });
    },
    [paymentRouteId, sourceCurrencyId]
  );

  /**
   * Initiates the deposit with Alfred — returns a redirect URL the
   * user should be sent to (Alfred's hosted onramp UI).
   */
  const initiateDeposit = useCallback(
    async ({ exchange, sessionId }: { exchange: string; sessionId: string }) => {
      const redirectUrls = generateDepositCallbackUrls();

      return await alfredService.initiateDeposit({
        exchange,
        session_id: sessionId,
        ...redirectUrls,
        // Country-specific extras forwarded only when set — Alfred
        // ignores unknown keys but the BE DTO only accepts a 4-digit
        // numeric and would 422 on whitespace, so we drop falsy values.
        ...(bankId ? { bank_id: bankId } : {}),
      });
    },
    [bankId],
  );

  /**
   * Main handler for initiating the deposit process
   * Creates session, initiates deposit with Ozow, and navigates to redirect page
   */
  const handleInitiateDeposit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      try {
        setIsLoading(true);
        setError(null);

        const sessionData = await createSession(amount);

        if (sessionData?.session_id && sessionData?.exchange) {
          setSessionData(sessionData);

          const alfredData = await initiateDeposit({
            exchange: sessionData.exchange,
            sessionId: sessionData.session_id,
          });

          // Inline rails (SPEI/PIX/COELSA/ACH) — Alfred returns full bank
          // instructions in the response and the widget renders them in
          // place of the form.
          if (alfredData?.transaction?.fiatPaymentInstructions) {
            setPaymentInstructions(alfredData.transaction);
          } else if (alfredData?.redirectUrl) {
            // Hosted-onramp rails — fall back to the legacy redirect flow.
            navigate(`${ERoutes.PaymentRedirect}?redirectUrl=${encodeURIComponent(alfredData.redirectUrl)}`);
          }
        }
      } catch (error) {
        console.error("Error initiating deposit:", error);
        setError(_t("ERRORS.FAILED_TO_INITIATE_DEPOSIT"));
      } finally {
        setIsLoading(false);
      }
    },
    [amount, createSession, initiateDeposit, navigate, setError, setIsLoading, setPaymentInstructions, setSessionData]
  );

  return {
    handleAmountChange,
    handleInitiateDeposit,
  };
};
